import { useState, useEffect } from 'react';
import { 
  FileText, 
  AlertTriangle, 
  Search, 
  Filter, 
  Download,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  AlertCircle,
  Zap
} from 'lucide-react';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'success';
  category: string;
  message: string;
  details?: any;
  user_id?: string;
  ip_address?: string;
}

interface WebhookLog {
  id: string;
  webhook_type: string;
  webhook_url: string;
  payload: any;
  response_status: number | null;
  response_body: string | null;
  success: boolean;
  error_message: string | null;
  created_at: string;
}

const Logs = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [errors, setErrors] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'logs' | 'errors'>('logs');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [backendAvailable, setBackendAvailable] = useState(false);

  useEffect(() => {
    loadLogs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadLogs = async () => {
    try {
      // Check if backend is available
      const healthResponse = await fetch('/api/health');
      if (healthResponse.ok) {
        setBackendAvailable(true);
        await Promise.all([
          loadSystemLogs(),
          loadWebhookLogs(),
          loadErrorLogs()
        ]);
      } else {
        setBackendAvailable(false);
        generateMockLogs();
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setBackendAvailable(false);
      generateMockLogs();
    } finally {
      setLoading(false);
    }
  };

  const loadSystemLogs = async () => {
    try {
      // In a real implementation, this would fetch from a logs API
      // For now, we'll generate some sample logs
      const sampleLogs: LogEntry[] = [
        {
          id: '1',
          timestamp: new Date().toISOString(),
          level: 'info',
          category: 'system',
          message: 'Server started successfully',
          details: { port: 3001, environment: 'production' }
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          level: 'success',
          category: 'api',
          message: 'Message processed successfully',
          user_id: 'user-123',
          ip_address: '192.168.1.1'
        },
        {
          id: '3',
          timestamp: new Date(Date.now() - 600000).toISOString(),
          level: 'warning',
          category: 'webhook',
          message: 'Webhook response timeout',
          details: { url: 'https://example.com/webhook', timeout: 10000 }
        }
      ];
      setLogs(sampleLogs);
    } catch (error) {
      console.error('Error loading system logs:', error);
    }
  };

  const loadWebhookLogs = async () => {
    try {
      const response = await fetch('/api/webhook-logs');
      if (response.ok) {
        const data = await response.json();
        setWebhookLogs(data.data || []);
      }
    } catch (error) {
      console.error('Error loading webhook logs:', error);
      // Generate sample webhook logs
      const sampleWebhookLogs: WebhookLog[] = [
        {
          id: '1',
          webhook_type: 'response',
          webhook_url: 'https://example.com/webhook/response',
          payload: { type: 'message_processed', user_id: 'user-123' },
          response_status: 200,
          response_body: '{"status":"ok"}',
          success: true,
          error_message: null,
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          webhook_type: 'list',
          webhook_url: 'https://example.com/webhook/list',
          payload: { type: 'shopping_list_updated', list_id: 'list-456' },
          response_status: 500,
          response_body: '{"error":"Internal server error"}',
          success: false,
          error_message: 'HTTP 500 Internal Server Error',
          created_at: new Date(Date.now() - 300000).toISOString()
        }
      ];
      setWebhookLogs(sampleWebhookLogs);
    }
  };

  const loadErrorLogs = async () => {
    try {
      // Filter logs for errors only
      const errorLogs = logs.filter(log => log.level === 'error');
      
      // Add some sample error logs
      const sampleErrors: LogEntry[] = [
        {
          id: 'err-1',
          timestamp: new Date(Date.now() - 120000).toISOString(),
          level: 'error',
          category: 'openai',
          message: 'OpenAI API key invalid',
          details: { error: 'Authentication failed', status: 401 }
        },
        {
          id: 'err-2',
          timestamp: new Date(Date.now() - 240000).toISOString(),
          level: 'error',
          category: 'woocommerce',
          message: 'WooCommerce connection failed',
          details: { error: 'ECONNREFUSED', url: 'https://store.example.com' }
        },
        {
          id: 'err-3',
          timestamp: new Date(Date.now() - 360000).toISOString(),
          level: 'error',
          category: 'database',
          message: 'Database query timeout',
          details: { query: 'SELECT * FROM users', timeout: 30000 }
        }
      ];
      
      setErrors([...errorLogs, ...sampleErrors]);
    } catch (error) {
      console.error('Error loading error logs:', error);
    }
  };

  const generateMockLogs = () => {
    const mockLogs: LogEntry[] = [
      {
        id: 'mock-1',
        timestamp: new Date().toISOString(),
        level: 'warning',
        category: 'system',
        message: 'Backend not available - showing demo data',
        details: { demo: true }
      },
      {
        id: 'mock-2',
        timestamp: new Date(Date.now() - 60000).toISOString(),
        level: 'info',
        category: 'frontend',
        message: 'Frontend loaded successfully',
        details: { version: '1.0.0' }
      }
    ];
    
    setLogs(mockLogs);
    setErrors([]);
    setWebhookLogs([]);
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
    const matchesCategory = categoryFilter === 'all' || log.category === categoryFilter;
    
    return matchesSearch && matchesLevel && matchesCategory;
  });

  const filteredErrors = errors.filter(error => 
    error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    error.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'info':
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'info':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const exportLogs = () => {
    const dataToExport = activeTab === 'logs' ? filteredLogs : filteredErrors;
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const categories = ['all', ...Array.from(new Set(logs.map(log => log.category)))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
          <p className="text-gray-600 mt-1">Monitor system activities, webhooks, and errors</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadLogs}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={exportLogs}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Backend Status */}
      {!backendAvailable && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Demo Mode
              </h3>
              <p className="text-yellow-700">
                Backend not available. Showing sample log data for demonstration purposes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Errors</p>
              <p className="text-2xl font-bold text-gray-900">{errors.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Webhooks</p>
              <p className="text-2xl font-bold text-gray-900">{webhookLogs.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {logs.length > 0 ? Math.round(((logs.length - errors.length) / logs.length) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'logs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              All Logs ({logs.length})
            </button>
            <button
              onClick={() => setActiveTab('errors')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'errors'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Errors ({errors.length})
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {activeTab === 'logs' && (
              <>
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Levels</option>
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Logs Content */}
        <div className="p-6">
          {activeTab === 'logs' && (
            <div className="space-y-4">
              {filteredLogs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || levelFilter !== 'all' || categoryFilter !== 'all'
                      ? 'Try adjusting your search filters'
                      : 'Logs will appear here as the system operates'}
                  </p>
                </div>
              ) : (
                filteredLogs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getLevelIcon(log.level)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelColor(log.level)}`}>
                              {log.level.toUpperCase()}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                              {log.category}
                            </span>
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatTimestamp(log.timestamp)}
                            </div>
                          </div>
                          <p className="text-sm text-gray-900 font-medium">{log.message}</p>
                          {log.details && (
                            <pre className="mt-2 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                          {(log.user_id || log.ip_address) && (
                            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                              {log.user_id && <span>User: {log.user_id}</span>}
                              {log.ip_address && <span>IP: {log.ip_address}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'errors' && (
            <div className="space-y-4">
              {filteredErrors.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No errors found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm 
                      ? 'No errors match your search'
                      : 'Great! No errors have been logged recently'}
                  </p>
                </div>
              ) : (
                filteredErrors.map((error) => (
                  <div key={error.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            ERROR
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                            {error.category}
                          </span>
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatTimestamp(error.timestamp)}
                          </div>
                        </div>
                        <p className="text-sm text-red-900 font-medium">{error.message}</p>
                        {error.details && (
                          <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(error.details, null, 2)}
                          </pre>
                        )}
                        {(error.user_id || error.ip_address) && (
                          <div className="mt-2 flex items-center space-x-4 text-xs text-red-600">
                            {error.user_id && <span>User: {error.user_id}</span>}
                            {error.ip_address && <span>IP: {error.ip_address}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Webhook Logs Section */}
      {webhookLogs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Webhook Activity</h3>
          <div className="space-y-3">
            {webhookLogs.slice(0, 5).map((webhook) => (
              <div key={webhook.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {webhook.success ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {webhook.webhook_type} → {webhook.webhook_url}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatTimestamp(webhook.created_at)}
                      {webhook.response_status && ` • HTTP ${webhook.response_status}`}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  webhook.success 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {webhook.success ? 'Success' : 'Failed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;